import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Inquiry } from '@tv3/store/inquiry/inquiry.model'
import { Message, plainTextMessage } from '@tv3/store/message/message.model'
import * as R from 'ramda'
import { AddMessage, AddMessageComplete, GetBookingMessages } from '@tv3/store/message/message.actions';
import {
  Account,
  AccountGuard,
  AlertDialogService,
  Attachment,
  ChannelService,
  epochToView,
  SaveForm,
  selectAccount,
  selectUsers,
  sortAscend,
  Toaster,
} from '@tokeet/tv3-platform'
import * as jQuery from 'jquery'
import { MessageForm } from '@tv3/interfaces/forms/message.form'
import { select, Store } from '@ngrx/store'
import { selectBrandingPreference } from '@tv3/store/preferences/preferences.selectors'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import * as fromRoot from '@tv3/store/state'
import { Branding } from '@tv3/models/account/branding'
import { AttachmentDialogService } from '@tv3/shared/attachment/attachment-dialog.service'
import { selectTemplatesByTypes, Template, TemplateType } from '@tokeet-frontend/templates'
import { isAddingMessage, selectInquiries, selectInquiry } from '@tv3/store/inquiry/inquiry.selectors'
import { DataDictService } from '@tv3/services/data-dict.service'
import { Website } from '@tv3/store/website/website.model'
import { selectWebsitesForRental } from '@tv3/store/website/website.selectors'
import { Actions, ofType } from '@ngrx/effects'
import { debounceTime, filter, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators'
import { DataCheckerService } from '@tokeet/tv3-platform'
import { TemplateGuard } from '@tv3/guards/template.guard'
import { UserGuard } from '@tv3/guards/user.guard'
import { PreferencesGuard } from '@tv3/guards/preferences.guard'
import { Guest } from '@tv3/store/guest/guest.model'
import { SearchInquiries } from '@tv3/store/inquiry/inquiry.actions'
import { defaultTinyMCEOptions } from '@tv3/constants/default-tinymce-options'
import { InquiryGuard } from '@tv3/guards/inquiry.guard'
import { AuthService } from '@tv3/services/auth.service'
import { DataDictionaryDialogService } from '@tv3/shared/data-dictionary-dialog/data-dictionary-dialog.service'
import { WebsiteGuard } from '@tv3/guards/website.guard'
import { MatRipple } from '@angular/material/core'
import { GetGuest } from '@tv3/store/guest/guest.actions'
import { ActionFailed, secureFileLink } from '@tokeet-frontend/utility'
import { EditorComponent } from '@tinymce/tinymce-angular'
import { AiResponderService } from '@tv3/containers/ai-responder/store/ai-responder.service'
import { environment } from '@tv3/environments/environment'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { SessionStorage } from '@tv3/services/utils/storage.service'

export function getLocalBookingMessageDraftKey(inquiryId: string) {
  return `${inquiryId}-message-draft`
}

@Component({
  selector: 'app-inquiry-message-form',
  templateUrl: './inquiry-message-form.component.html',
  host: { class: 'd-flex flex-column message-editor comment-composer-bottom-fixed' },
  styleUrls: ['./inquiry-message-form.component.scss'],
})
export class InquiryMessageFormComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('ccRipple', { read: MatRipple }) ccRipple: MatRipple
  @ViewChild('bccRipple', { read: MatRipple }) bccRipple: MatRipple

  @ViewChild('tinyMceEditor') tinyMceEditor: EditorComponent

  @Input() inquiry: Inquiry
  @Input() guest?: Guest
  @Input() draftMessage?: Message

  @Output() close = new EventEmitter()
  @Output() popup = new EventEmitter()

  @Input()
  @HostBinding('class.popup')
  isPopup = false

  get localBookingMessageDraftKey() {
    return getLocalBookingMessageDraftKey(this.inquiry.id)
  }

  hasCC = false
  hasBCC = false

  form = this.fb.group({
    subject: [''],
    cc: [[]],
    bcc: [[]],
    messageHtml: ['', [Validators.required]],
    messageText: [''],
    addCost: [false],
    template: [''],
    attachments: [],
  })

  inquiryCtrl = new FormControl('')

  ccUsers: { name: string; email: string; tag?: boolean }[] = []

  branding: Branding

  restrictedEmail: boolean

  editorOptions = defaultTinyMCEOptions

  channel: string | boolean
  rentalWebsites: Website[] = []
  account: Account

  isAIGenerating = false
  isAISubscriptionValid = false
  showEditor = false

  users$ = this.store.pipe(select(selectUsers))
  inquiries$: Observable<{ id: string; name: string }[]>
  templates$ = this.store.pipe(select(selectTemplatesByTypes, { types: [TemplateType.Email, TemplateType.Contract] }))
  isAddingMessage$ = this.store.pipe(select(isAddingMessage))

  inquirySearch = new BehaviorSubject<string>('')

  private destroy$ = new Subject()

  constructor(
    private fb: FormBuilder,
    private toaster: Toaster,
    private actions$: Actions,
    private storage: SessionStorage,
    private dataChecker: DataCheckerService,
    private attachmentDialog: AttachmentDialogService,
    private channelService: ChannelService,
    private alertDialog: AlertDialogService,
    private dataDictService: DataDictService,
    private auth: AuthService,
    private dataDictionaryDialogService: DataDictionaryDialogService,
    private store: Store<fromRoot.State>,
    private aiResponder: AiResponderService
  ) {
    this.dataChecker.check([TemplateGuard, UserGuard, PreferencesGuard, InquiryGuard, AccountGuard, WebsiteGuard])
  }

  ngOnInit() {
    this.store.dispatch(GetGuest({ id: this.inquiry.guestId }))
    this.inquirySearch
      .pipe(
        filter((name) => !R.isEmpty(name)),
        debounceTime(375),
        takeUntil(this.destroy$)
      )
      .subscribe((term) => {
        this.store.dispatch(SearchInquiries({ term }))
      })

    this.store
      .pipe(select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)), takeUntil(this.destroy$))
      .subscribe((res) => {
        this.isAISubscriptionValid = !!res?.isValid
      })

    this.store.pipe(select(selectInquiry, { id: this.inquiry.id }), takeUntil(this.destroy$)).subscribe((inquiry) => {
      this.inquiry = inquiry
    })

    this.store
      .pipe(select(selectWebsitesForRental, { id: this.inquiry.rentalId }), takeUntil(this.destroy$))
      .subscribe((webs) => {
        this.rentalWebsites = webs
      })

    this.store.pipe(select(selectAccount(this.auth.user.account)), takeUntil(this.destroy$)).subscribe((account) => {
      this.account = account
    })

    this.actions$.pipe(ofType(AddMessageComplete), takeUntil(this.destroy$)).subscribe(() => {
      this.storage.remove(this.localBookingMessageDraftKey)
      this.close.emit(true)
    })

    this.initForm(this.inquiry)
    this.checkIsChannel()
    this.form
      .get('messageHtml')
      .valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe((msg) => {
        this.storage.set(this.localBookingMessageDraftKey, msg)
      })

    if (this.guest) {
      this.inquiryCtrl.patchValue(this.inquiry.id)
      this.inquiries$ = this.store.pipe(
        select(selectInquiries),
        map((inquiries) => R.filter((i: Inquiry) => i.guestId === this.guest.id, inquiries)),
        map((inquiries) =>
          R.map((i: Inquiry) => {
            const createdView = epochToView(i.created)
            return {
              id: i.id,
              name: `${R.pathOr('', ['guestDetails', 'name'], i)} / ${R.pathOr(
                '',
                ['rental', 'name'],
                i
              )} / ${createdView}`,
            }
          }, inquiries)
        )
      )
      this.inquiryCtrl.valueChanges
        .pipe(
          switchMap((id) => this.store.pipe(select(selectInquiry, { id }))),
          takeUntil(this.destroy$)
        )
        .subscribe((inquiry) => {
          this.initForm(inquiry)
        })
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.showEditor = true
    }, 0)
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  enableQuote(inquiry: Inquiry) {
    const source = inquiry.inquirySource
    return !/airbnb|booking/gi.test(source)
  }

  initForm(inquiry: Inquiry) {
    const localMsg = this.storage.get(this.localBookingMessageDraftKey, '')
    this.form.patchValue({
      subject: this.getSubject(inquiry),
      messageHtml: this.isPopup ? localMsg : this.draftMessage?.messageHtml || '',
    })
    this.ccUsers = this.getCCUsers(inquiry, this.ccUsers)
  }

  onToggleCC() {
    this.hasCC = !this.hasCC
    if (this.hasCC) {
      setTimeout(() => this.launchRipple(this.ccRipple), 0)
    }
  }

  onToggleBCC() {
    this.hasBCC = !this.hasBCC
    if (this.hasBCC) {
      setTimeout(() => this.launchRipple(this.bccRipple), 0)
    }
  }

  onAttach() {
    const items = this.form.get('attachments').value || []
    this.attachmentDialog.open().subscribe((attachments: Attachment[]) => {
      this.form.patchValue({ attachments: [...items, ...attachments] })
      this.insertLinks(attachments)
    })
  }

  onDataDictionary() {
    this.dataDictionaryDialogService.open().subscribe((tag) => {
      try {
        this.dataDictService
          .resolve(tag, {
            inquiry: this.inquiry,
            rental: this.inquiry.rental,
            guest: this.inquiry.guest,
            websites: this.rentalWebsites,
            account: this.account,
          })
          .subscribe((compiledTag) => {
            this.tinyMceEditor['editor'].execCommand('mceInsertContent', false, compiledTag)
          })
      } catch (e) {}
    })
  }

  onPopup() {
    this.popup.emit(true)
    document.body.classList.add('message-form-popup')
  }

  onClose() {
    this.close.emit(true)
    document.body.classList.remove('message-form-popup')
  }

  onAiResponder() {
    if (!this.isAISubscriptionValid) {
      window.open(`${environment.appStoreUrl}/login?redirect=/details/tokeet_ai&token=${this.auth.token}`, '_blank')
      return
    }

    this.isAIGenerating = true
    this.aiResponder
      .generateReply({ account: this.inquiry.account, inquiryId: this.inquiry.id, convert: 'html', create: 'draft' })
      .pipe(finalize(() => (this.isAIGenerating = false)))
      .subscribe(
        ({ message }) => {
          const messageCtrl = this.form.get('messageHtml')
          messageCtrl.setValue(`<p>${message.replace(/\n/g, '<br>')}</p>`)
          this.store.dispatch(GetBookingMessages({ bookingId: this.inquiry.id }))
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onTemplateChange([template]: [Template]) {
    if (template) {
      this.dataDictService
        .resolve(template.body, {
          inquiry: this.inquiry,
          rental: this.inquiry.rental,
          guest: this.inquiry.guest,
          websites: this.rentalWebsites,
          account: this.account,
        })
        .subscribe((compiledMessage) => {
          this.form.patchValue({ template, subject: template.subject, messageHtml: compiledMessage || '' })
        })
    }
  }

  onSend(form: FormGroup) {
    const msgCtrl = this.form.get('messageHtml')
    if (msgCtrl.invalid) {
      this.toaster.warning('Message content is required')
      return
    }
    const messageHtml = msgCtrl.value
    if (this.isMessageValid(this.inquiry, messageHtml)) {
      this.form.patchValue({
        attachments: this.getPresentAttachments(messageHtml),
        messageText: this.tinyMceEditor.editor.getContent({ format: 'text' }),
      })
      const formValues = form.getRawValue() as MessageForm
      this.store.dispatch(
        AddMessage({
          inquiry: this.inquiry,
          form: {
            ...formValues,
          },
          channel: this.channel,
        })
      )
    }
  }

  private launchRipple(ripple: MatRipple) {
    ripple.disabled = true
    ripple.launch({
      centered: true,
      color: '#1cbdd31a',
    })
  }

  private checkIsChannel() {
    this.channel = this.channelService.channelFromEmail(this.inquiry.guest.primaryEmail)

    if (!!this.channel) {
      this.restrictedEmail = true
      this.alertDialog.open({
        title: 'Limited messaging ability',
        body: `Your message will be routed through ${this.channel}.<br>As a result you are restricted in what you may include<br>in your message. Links, attachments, and email addresses<br>are not allowed. ${this.channel} will block any message containing<br>restricted content.`,
      })
    }
  }

  private getMessageText(messageHtml: string): string {
    return plainTextMessage(String(this.getMessageHtmlWithoutAttachments(messageHtml)))
  }

  private getMessageHtmlWithoutAttachments(messageHtml: string): string {
    return jQuery(`<div>${messageHtml}</div>`).find('.attachment').remove().end().html()
  }

  private isMessageValid(inquiry: Inquiry, messageHtml: string) {
    const messageHtmlRaw = this.getMessageHtmlWithoutAttachments(messageHtml)
    const messageText = this.getMessageText(messageHtml)

    if (!inquiry.id || !inquiry.convoId || !inquiry.guestId) {
      this.alertDialog.open({
        title: 'Error',
        body: 'There is an error in your inquiry.',
      })
      return false
    } else if (!inquiry.rentalId) {
      this.alertDialog.open({
        title: 'Error',
        body: 'You must first select a rental for this inquiry.',
      })
      return false
    } else if (!messageText || !messageHtmlRaw || messageHtmlRaw.length < 3 || messageText.length < 3) {
      this.alertDialog.open({
        title: 'Error',
        body: 'Your message must be over 2 characters.',
      })
      return false
    }

    return true
  }

  addNewCC = (email: string) => {
    email = email.toLowerCase()
    const EMAIL_REGEXP =
      /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
    if (EMAIL_REGEXP.test(email)) {
      const newCC = { name: email, email: email, tag: true }
      this.ccUsers = R.append(newCC, <any>this.ccUsers)
      return newCC
    } else {
      this.alertDialog.open({
        title: 'Cannot send to that email.',
        body: 'The email you entered is invalid. Tokeet cannot send messsages to invalid email addresses. Please enter a valid email address to add to the CC of this message.',
      })
    }
  }

  private insertLinks(attachmentResponse: Attachment[]) {
    let attachments = ''
    let body = ''

    R.forEach((file: Attachment) => {
      attachments += `<p>&#x1f4c2;&nbsp;&nbsp;<a href="${secureFileLink(
        file.url,
        this.inquiry?.guestId || this.guest?.id
      )}" target="_blank" id="${file.id}" class="attachment">${file.name}</a></p>`
    }, attachmentResponse || [])

    if (attachments.length > 0) {
      body += `<div class="attachments">${attachments}</div>`
    }

    const message = this.form.get('messageHtml').value
    this.form.patchValue({ messageHtml: `${message} ${body}` })
  }

  private getCCUsers(inquiry: Inquiry, ccUsers: { name: string; email: string }[]) {
    const newCcUsers = R.clone(ccUsers)
    R.forEach((m: Message) => {
      if (m.type === 1) {
        const user = { name: m.name, email: m.replyTo }
        if (
          R.isNil(R.find((u) => u.name === user.name && u.email === user.email, newCcUsers)) &&
          inquiry.guest.primaryEmail !== m.replyTo
        ) {
          newCcUsers.push(user)
        }
      }
    }, R.defaultTo([], inquiry.messages))
    return <any>sortAscend('name')(newCcUsers)
  }

  private getSubject(inquiry: Inquiry): string {
    const rentalView = R.pathOr('', ['rentalView'], inquiry)
    const rentalName = R.pathOr(rentalView, ['rental', 'name'], inquiry)
    if (inquiry) {
      return `Re: Your inquiry about ${rentalName} (${inquiry.guestNameView}) - ${inquiry.id.substr(
        inquiry.id.length - 5
      )}`
    } else {
      return 'Re: Your inquiry'
    }
  }

  private getPresentAttachments(messageHTML: any): Attachment[] {
    const attachedFiles = []
    const attachments = this.form.get('attachments').value
    if (attachments && attachments.length > 0) {
      R.forEach((file: Attachment) => {
        if (jQuery('#' + file.id, messageHTML).length > 0) {
          attachedFiles.push(file)
        }
      }, attachments)
    }
    return attachedFiles
  }
}
